<template>
  <v-container fluid pa-0>
    <v-row>
      <v-col
        cols="12"
        class="qr-scanner"
        :class="[{ 'scan-success': showSuccess }, { 'scan-error': showError }]"
      >
        <v-progress-circular v-if="loading" indeterminate />
        <template v-if="showOverlay">
          <div class="overlay"></div>
          <v-icon color="white" class="state-icon">{{
            showError ? "clear" : "check_circle_outline"
          }}</v-icon>
        </template>
        <html5-qr-stream @decoded="decoded($event)" />
        <slot
          name="error"
          :error-messages="internalErrorMessages"
          :current-scanned="currentScanned"
        >
          <barcode-scanner-error-dialog
            :visible="internalErrorMessages.length > 0"
            :error-messages="internalErrorMessages"
            @accept="internalErrorMessages = []"
            @cancel="internalErrorMessages = []"
          />
        </slot>
      </v-col>
    </v-row>
  </v-container>
</template>
<script lang="ts">
import Html5QrStream from "@/components/controls/device/barcodes/Html5QrStream.vue";
import BarcodeScannerErrorDialog from "@/components/controls/device/barcodes/BarcodeScannerErrorDialog.vue";
import appStore from "@/store/modules/appStore";
import { stripUrlPartsFromBarcode } from "@/components/controls/device/BmBarcodeInput.vue";
import { barcodeSuccess, barcodeFailure } from "@/assets/sounds/barcodeSounds";
import type { PropType } from "vue";
import { defineComponent } from "vue";

async function playScanSound(success: boolean) {
  const sound = success ? new Audio(barcodeSuccess) : new Audio(barcodeFailure);
  try {
    await sound.play();
  } catch (e) {
    // leave empty - no need to throw error/alert user
  }
}

export default defineComponent({
  name: "BarcodeScanner2d",
  components: {
    BarcodeScannerErrorDialog,
    Html5QrStream,
  },
  props: {
    timeoutMs: {
      type: Number,
      default: () => 1000,
    },
    rules: {
      type: Array as PropType<((item: any) => boolean | string)[]>,
      default: () => [],
    },
    playSounds: {
      type: Boolean,
      default: true,
    },
    snackbarTimeout: {
      type: Number,
      default: 4000,
    },
  },
  data() {
    return {
      loading: false,
      showSuccess: false,
      internalErrorMessages: [] as string[],
      currentScanned: "",
    };
  },
  computed: {
    showError(): any {
      return this.internalErrorMessages.length;
    },
    showOverlay(): any {
      return this.showError || this.showSuccess;
    },
  },
  watch: {
    loading: {
      // @ts-ignore
      handler: "isLoadingWatcher",
    },
  },
  methods: {
    async decoded(decodedValue: string) {
      // Reject another event while we are still processing the last or we are showing errors
      if (this.showOverlay) {
        return;
      }

      // Strip the url parts out internally so we don't push errors
      decodedValue = stripUrlPartsFromBarcode(decodedValue);

      this.currentScanned = decodedValue;
      this.internalErrorMessages = [];

      this.rules.forEach((rule) => {
        let result = rule(this.currentScanned);
        if (typeof result === "string") {
          this.internalErrorMessages.push(result);
        } else if (!result) {
          this.internalErrorMessages.push(this.$m.containers_unknown_error);
        }
      });

      if (this.playSounds) {
        await playScanSound(!this.showError);
      }

      if (!this.showError) {
        // eslint-disable-next-line vue/no-deprecated-dollar-listeners-api
        if (!this.$listeners["scan-notification"]) {
          appStore.pushSnackbarConfig({
            text: this.$m.barcode_last_scanned + " " + this.currentScanned,
            timeout: this.snackbarTimeout,
          });
        } else {
          this.$emit("scan-notification", this.currentScanned);
        }

        this.showSuccess = true;
        setTimeout(() => {
          this.showSuccess = false;
          this.$emit("valid-scanned", this.currentScanned);
        }, this.timeoutMs);
      }
    },
    isLoadingWatcher() {
      this.$emit("loading", this.loading);
    },
  },
});
</script>

<style scoped lang="sass">
@import "src/sass/colours"

$successIconSize: 100px

.qr-scanner
  position: relative
  border: 5px solid transparent

.qr-scanner video
  max-width : 100%
  display : block

.qr-scanner.scan-success
  border: 5px solid $bm-color-success-medium

.qr-scanner.scan-error
  border: 5px solid $bm-color-error-medium

.qr-scanner .overlay
  height: 100%
  width: 100%
  position: absolute
  top: 0
  left: 0
  z-index: 2
  opacity: 0.50

.scan-success .overlay
  background: $bm-color-success-medium

.scan-error .overlay
  background: $bm-color-error-medium


// Center Alignment of absolute items are calculated from their top left corner
// Add Half of their size
.v-progress-circular, .v-icon.state-icon
  z-index: 3
  position: absolute
  left: 50%
  top: 50%
  vertical-align: middle

.v-icon.state-icon
  font-size: $successIconSize
  margin-top: calc(-#{$successIconSize} / 2)
  margin-left: calc(-#{$successIconSize} / 2)
</style>
